import { useEffect, useState } from 'react'
import { Text } from '@carouselgg/nexus/text'
import { Button } from '@carouselgg/nexus/button'
import { Callout } from '@carouselgg/nexus/callout'
import { answerSideSwap, getSideSwap } from '../api/sideSwaps'
import type { SideSwapDetail } from '../api/sideSwaps'
import { swapExplanation, swapOutcome, swapSides } from './sideSwapModel'
import styles from './TeamInviteAnswer.module.css'

const errorText = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong')

/**
 * Answers the other captain's request to swap starting sides, right from its
 * notification: what the swap would do, their message, then Agree or Keep it.
 * The league sees an agreed swap; an answered request just says how it went.
 */
export function SideSwapAnswer({ orgId, swapId }: { orgId: string; swapId: string }) {
  const [detail, setDetail] = useState<SideSwapDetail | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    getSideSwap(orgId, swapId)
      .then((d) => live && setDetail(d))
      .catch((err) => live && setProblem(errorText(err)))
    return () => { live = false }
  }, [orgId, swapId])

  const answer = async (accept: boolean) => {
    if (!detail) return
    setBusy(true)
    setProblem(null)
    try {
      const updated = await answerSideSwap(orgId, swapId, accept)
      setDetail({ ...detail, status: updated.status, answered_at: updated.answered_at })
    } catch (err) {
      setProblem(errorText(err))
    }
    setBusy(false)
  }

  if (!detail) return problem ? <Callout variant="error">{problem}</Callout> : <Text size="sm" muted>Loading the request…</Text>
  const sides = swapSides(detail)
  const mine = detail.requested_by_team_id === detail.team_id

  return (
    <div className={styles.answer}>
      {sides && <Text size="sm">{swapExplanation(detail.map, sides, detail.game_number)}</Text>}
      {detail.message && <p className={styles.quote}>“{detail.message}”</p>}
      {detail.status === 'pending' && !mine ? (
        <>
          <div className={styles.buttons}>
            <Button variant="primary" onClick={() => void answer(true)} disabled={busy}>Agree to swap</Button>
            <Button variant="secondary" onClick={() => void answer(false)} disabled={busy}>Keep it</Button>
          </div>
          <Text size="xs" muted>The league sees the change. They can also set sides themselves.</Text>
        </>
      ) : (
        <Text size="sm" weight="semibold">{mine && detail.status === 'pending' ? 'Waiting on the other captain.' : swapOutcome(detail.status)}</Text>
      )}
      {problem && <Callout variant="error">{problem}</Callout>}
    </div>
  )
}
